import {
	ConstructionSiteOffer,
	DerogationType,
	DerogationTypeValues,
} from "@dune/types";
import { isOfferWarrantyType } from "@dune/vars";
import {
	Button,
	Card,
	CardContent,
	Checkbox,
	cn,
	DateInputComponent,
	Input,
	Label,
	Separator,
	StyledSelect,
} from "@dune/ui";
import { DerogationsForm } from "../DerogationStep";
import { PricingDerogationForm } from "./PricingDerogationForm";
import { useMemo } from "react";
import { parseDate } from "@internationalized/date";
import { differenceInMonths } from "date-fns";
import { useShouldDisplayHTAmount } from "../../../../../hooks/useParticipantToSubmissionForm";
import { PriceInput } from "../../../../../components/inputs/PriceInput";

interface DerogationsCardProps {
	offers?: ConstructionSiteOffer[];
	onFormChange: (newForm: DerogationsForm) => void;
	onRemoveDerogation: (index: number) => void;
	onAddDerogation: () => void;
	derogationsForm: DerogationsForm;
	constructionSiteStartDate: string | null;
	showErrors?: boolean;
	constructionSiteId: number;
}

export type DerogationsCardHandle = {
	submitPricingForms: () => void;
};

const OfferSelection = ({
	offers,
	selectedOfferIds,
	onSelectionChange,
}: {
	offers: ConstructionSiteOffer[];
	selectedOfferIds: number[];
	onSelectionChange: (newSelectedIds: number[]) => void;
}) => (
	<div className="flex flex-col gap-3">
		<div className="text-primary text-base uppercase">
			Offre(s) concernée(s)
		</div>
		<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			{offers
				.filter((offer) => {
					const warrantyType = offer.binderWarranty?.warrantyType;
					return isOfferWarrantyType(warrantyType);
				})
				.map((offer) => (
					<Card key={offer.id} noShadow className="p-4">
						<div className="flex items-center justify-between">
							<div className="text-muted-foreground text-sm">
								{offer.binderWarranty?.shortName ??
									offer.binderWarranty?.name ??
									"Offre"}
							</div>
							<div className="flex items-center gap-2">
								<Checkbox
									checked={selectedOfferIds.includes(offer.id)}
									onCheckedChange={(checked) => {
										const next = checked
											? Array.from(new Set([...selectedOfferIds, offer.id]))
											: selectedOfferIds.filter((id) => id !== offer.id);
										onSelectionChange(next);
									}}
								/>
								<span className="text-sm text-muted-foreground">Inclure</span>
							</div>
						</div>
					</Card>
				))}
		</div>
	</div>
);

export const DerogationsCard = ({
	offers,
	onFormChange,
	onRemoveDerogation,
	onAddDerogation,
	derogationsForm,
	constructionSiteStartDate,
	showErrors = false,
	constructionSiteId,
}: DerogationsCardProps) => {
	const derogationTypeOptions = useMemo(
		() => [
			{ label: "Tarification", value: DerogationType.PRICING },
			{
				label: "Coût de construction",
				value: DerogationType.CONSTRUCTION_COST,
			},
			{ label: "Durée du chantier", value: DerogationType.DURATION },
		],
		[],
	);

	const selectedOffersIds = derogationsForm.selectedOffersIds;
	const { shouldDisplayHT } = useShouldDisplayHTAmount(constructionSiteId);
	return (
		<Card>
			<CardContent className="flex flex-col gap-6">
				{offers && (
					<OfferSelection
						offers={offers}
						selectedOfferIds={derogationsForm.selectedOffersIds}
						onSelectionChange={(next) =>
							onFormChange({ ...derogationsForm, selectedOffersIds: next })
						}
					/>
				)}
				{derogationsForm.derogations.length < 3 && (
					<Button onClick={onAddDerogation}>Ajouter une dérogation</Button>
				)}
				{derogationsForm.derogations.map((derogation, index) => {
					const newDurationMonths =
						derogation.newDeliveryDate && constructionSiteStartDate
							? differenceInMonths(
									new Date(derogation.newDeliveryDate),
									new Date(constructionSiteStartDate),
								)
							: undefined;
					const newDurationMonthsValue =
						newDurationMonths && newDurationMonths > 0
							? String(newDurationMonths)
							: "-";
					return (
						<div
							className="flex flex-col gap-6"
							key={`${derogation.type}-${index}`}
						>
							<Separator />
							<div className="flex items-center gap-3">
								<StyledSelect
									value={derogation.type ?? undefined}
									onValueChange={(value) =>
										onFormChange({
											...derogationsForm,
											derogations: derogationsForm.derogations.map(
												(previousDerogation, previousIndex) => {
													if (previousIndex === index) {
														return {
															...previousDerogation,
															type: value as DerogationTypeValues,
														};
													}
													return previousDerogation;
												},
											),
										})
									}
									options={derogationTypeOptions}
									placeholder="Sélectionner"
									error={
										showErrors && !derogation.type
											? "Veuillez sélectionner un type de dérogation."
											: undefined
									}
								/>

								<Button
									variant="outline"
									onClick={() => onRemoveDerogation(index)}
								>
									Supprimer
								</Button>
							</div>
							{derogation.type === DerogationType.DURATION && (
								<div className="flex flex-row gap-2">
									<div className="flex flex-col flex-1">
										<Label className="text-muted-foreground text-sm">
											Date de livraison prévisionnelle
										</Label>
										<DateInputComponent
											value={
												derogation.newDeliveryDate
													? parseDate(derogation.newDeliveryDate)
													: undefined
											}
											onChange={(value) =>
												onFormChange({
													...derogationsForm,
													derogations: derogationsForm.derogations.map(
														(previousDerogation, previousIndex) => {
															if (previousIndex === index) {
																return {
																	...previousDerogation,
																	newDeliveryDate: value?.toString() || "",
																};
															}
															return previousDerogation;
														},
													),
												})
											}
										/>
									</div>
									<div className="flex flex-col flex-1">
										<Label className="text-muted-foreground text-sm">
											Durée (mois)
										</Label>
										<Input value={newDurationMonthsValue} disabled />
									</div>
								</div>
							)}
							{derogation.type === DerogationType.CONSTRUCTION_COST && (
								<div className="flex flex-col gap-2">
									<Label className="text-muted-foreground text-sm">
										Coût de construction ({shouldDisplayHT ? "HT" : "TTC"})
									</Label>
									<PriceInput
										value={Number(derogation.newConstructionCost)}
										onChange={(value) =>
											onFormChange({
												...derogationsForm,
												derogations: derogationsForm.derogations.map(
													(previousDerogation, previousIndex) => {
														if (previousIndex === index) {
															return {
																...previousDerogation,
																newConstructionCost: value.toString(),
															};
														}
														return previousDerogation;
													},
												),
											})
										}
									/>
								</div>
							)}
						</div>
					);
				})}

				{derogationsForm.derogations &&
					derogationsForm.derogations.length > 0 && (
						<div className="flex flex-col gap-4">
							<Separator />
							<div
								className={cn(
									"grid gap-6",
									selectedOffersIds && selectedOffersIds.length > 1
										? "grid-cols-2"
										: "grid-cols-1",
								)}
							>
								{offers
									?.filter((offer) => selectedOffersIds.includes(offer.id))
									.map((offer) => {
										const pricingDetailsId = offer.pricingDetails.find(
											(pricingDetail) => pricingDetail.type === "INITIAL",
										)?.id;
										const offerWarrantyType =
											offer.binderWarranty?.warrantyType;
										return (
											offer.binderWarranty?.warrantyType &&
											isOfferWarrantyType(offerWarrantyType) &&
											pricingDetailsId && (
												<div className="flex flex-col gap-2" key={offer.id}>
													<PricingDerogationForm
														pricingDetailsId={pricingDetailsId}
														warrantyType={offerWarrantyType}
														onPricingDetailsChange={(pricingDetails) => {
															const newPricingDetails = {
																...derogationsForm.newPricingDetails,
																[offerWarrantyType]: pricingDetails,
															};
															onFormChange({
																...derogationsForm,
																newPricingDetails,
															});
														}}
													/>
												</div>
											)
										);
									})}
							</div>
						</div>
					)}
			</CardContent>
		</Card>
	);
};
